import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Header from '../../components/Header';
import ErrorBanner from '../../components/ErrorBanner';
import { colors, radius, shadows } from '../../styles/theme';
import { normalizeForSearch, searchMatchPre } from '../../utils/searchMatch';
import { formatSayi, formatPct } from '../../utils/analytics/format';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Locale formatlama tek kaynaktan (utils/analytics/format) — davranış birebir:
// formatTL = 2 hane sabit; formatNum = bare toLocaleString('tr-TR') parity (max 3, min 0)
const formatTL = (n) => formatSayi(Number(n || 0), { decimals: 2 });
const formatNum = (n) => formatSayi(Number(n || 0), { decimals: 3, minDecimals: 0 });

const today = new Date().toISOString().split('T')[0];
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};

const QUICK_RANGES = [
  { label: 'Bugün', start: today, end: today },
  { label: 'Son 7 Gün', start: daysAgo(7), end: today },
  { label: 'Son 30 Gün', start: daysAgo(30), end: today },
  { label: 'Bu Ay', start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0], end: today },
  { label: 'Geçen Ay', start: new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).toISOString().split('T')[0], end: new Date(new Date().getFullYear(), new Date().getMonth(), 0).toISOString().split('T')[0] },
];

const UrunSatisRaporuPage = ({ embedded = false }) => {
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [filtreler, setFiltreler] = useState({
    lokasyon_id: '',
    baslangic_tarihi: daysAgo(30),
    bitis_tarihi: today,
    gruplama: 'gun'
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [siralama, setSiralama] = useState('adet');
  const [hideZeroRevenue, setHideZeroRevenue] = useState(true);
  // tipFilter: ANA_URUN | YAN_URUN | ICECEK | TATLI | DIGER — KPI kartlar + dropdown ortak kontrol eder.
  // Eski raw `kategori` filtresi (TABAK/Mamul/Süt Ürünleri...) Recipe.tip ile aynı sözlüğe gidiyordu;
  // kullanıcı bu sözlüğü kafa karıştırıcı buluyordu (örn. TABAK bucket'ında köfte şurubu/irmık tatlısı).
  const [tipFilter, setTipFilter] = useState('');

  // Lokasyonları yükle
  useEffect(() => {
    fetch(`${API_URL}/api/lokasyonlar`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => setLokasyonlar(Array.isArray(d) ? d.filter(l => l.aktif !== false) : []))
      .catch(() => setLokasyonlar([]));
  }, []);

  // Veriyi yükle
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filtreler).forEach(([k, v]) => { if (v) params.set(k, v); });
      const res = await fetch(`${API_URL}/api/raporlar/urun-satis?${params}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const d = await res.json();
        setData(d);
        setError(null);
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || 'Sunucu hatası');
      }
    } catch (e) {
      setError('Bağlantı/sunucu hatası');
      logError('Yükleme hatası:', e);
    } finally {
      setLoading(false);
    }
  }, [filtreler]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleQuickRange = (range) => {
    setFiltreler(p => ({ ...p, baslangic_tarihi: range.start, bitis_tarihi: range.end }));
  };

  // Ürün tipi dropdown seçenekleri — KPI kartlarla aynı sözlük.
  // Raw kategori (Recipe.tip = TABAK/Mamul vb.) yerine computed urun_tipi kullanılır.
  const URUN_TIPI_OPTIONS = [
    { value: 'ANA_URUN', label: 'Ana Ürün' },
    { value: 'YAN_URUN', label: 'Yan Ürün' },
    { value: 'ICECEK', label: 'İçecek' },
    { value: 'TATLI', label: 'Tatlı' },
    { value: 'DIGER', label: 'Diğer' },
  ];

  const filteredUrunler = useMemo(() => {
    if (!data?.urunler) return [];
    let list = [...data.urunler];
    if (hideZeroRevenue) {
      list = list.filter(u => (u.ciro || 0) > 0);
    }
    if (tipFilter) {
      list = list.filter(u => u.urun_tipi === tipFilter);
    }
    if (search.trim()) {
      const s = normalizeForSearch(search);
      list = list.filter(u => searchMatchPre(u.urun_adi, s));
    }
    list.sort((a, b) => {
      if (siralama === 'adet') return b.adet - a.adet;
      if (siralama === 'ciro') return b.ciro - a.ciro;
      if (siralama === 'kar') return (b.kar || 0) - (a.kar || 0);
      if (siralama === 'kar_marj') return (b.kar_marj_pct || 0) - (a.kar_marj_pct || 0);
      return 0;
    });
    return list;
  }, [data, search, siralama, hideZeroRevenue, tipFilter]);

  const top10 = useMemo(() => filteredUrunler.slice(0, 10), [filteredUrunler]);
  const maxAdet = top10[0]?.adet || 1;

  const content = (
    <div style={{ padding: embedded ? 0 : '20px' }}>
      <ErrorBanner error={error} onRetry={loadData} />
      {/* Filtreler */}
      <div style={{ background: colors.surface, padding: '16px', borderRadius: radius.md, marginBottom: '16px', boxShadow: shadows.sm }}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
          {QUICK_RANGES.map(r => (
            <button key={r.label} onClick={() => handleQuickRange(r)}
              style={{
                padding: '6px 14px', border: `1px solid ${colors.border}`, borderRadius: radius.xl,
                background: (filtreler.baslangic_tarihi === r.start && filtreler.bitis_tarihi === r.end) ? '#1976d2' : 'white',
                color: (filtreler.baslangic_tarihi === r.start && filtreler.bitis_tarihi === r.end) ? 'white' : '#333',
                fontSize: '12px', cursor: 'pointer', fontWeight: '600'
              }}>
              {r.label}
            </button>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
          <div>
            <label style={labelStyle}>İşletme</label>
            <select value={filtreler.lokasyon_id} onChange={e => setFiltreler(p => ({ ...p, lokasyon_id: e.target.value }))} style={inputStyle}>
              <option value="">Tüm İşletmeler</option>
              {lokasyonlar.map(l => (
                <option key={l.id} value={l.id}>{l.ad}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Başlangıç</label>
            <input type="date" value={filtreler.baslangic_tarihi} onChange={e => setFiltreler(p => ({ ...p, baslangic_tarihi: e.target.value }))} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Bitiş</label>
            <input type="date" value={filtreler.bitis_tarihi} onChange={e => setFiltreler(p => ({ ...p, bitis_tarihi: e.target.value }))} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Trend Gruplama</label>
            <select value={filtreler.gruplama} onChange={e => setFiltreler(p => ({ ...p, gruplama: e.target.value }))} style={inputStyle}>
              <option value="gun">Günlük</option>
              <option value="hafta">Haftalık</option>
              <option value="ay">Aylık</option>
            </select>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
        <KpiCard label="Toplam Adet" value={formatNum(data?.toplam_adet || 0)} suffix="" color="#1976d2" />
        <KpiCard label="Toplam Ciro" value={formatTL(data?.toplam_ciro || 0)} suffix="₺" color="#2e7d32" />
        <KpiCard label="Ürün Çeşidi" value={data?.urun_sayisi || 0} suffix="" color="#7b1fa2" />
        <KpiCard label="Rapor Günü" value={data?.gun_sayisi || 0} suffix="gün" color="#f57c00" />
      </div>

      {/* Ürün Tipi Özet — tıklanabilir filtre */}
      {data?.tip_ozet && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', marginBottom: '16px' }}>
          {[
            { key: 'ANA_URUN', label: 'Ana Ürün', icon: '🍔', color: '#c62828' },
            { key: 'YAN_URUN', label: 'Yan Ürün', icon: '🍟', color: '#ef6c00' },
            { key: 'ICECEK', label: 'İçecek', icon: '🥤', color: '#1565c0' },
            { key: 'TATLI', label: 'Tatlı', icon: '🍰', color: '#ad1457' },
            { key: 'DIGER', label: 'Diğer', icon: '📦', color: '#546e7a' },
          ].map(t => {
            const info = data.tip_ozet[t.key] || { ciro: 0, adet: 0, pay_pct: 0, urun_sayisi: 0 };
            const active = tipFilter === t.key;
            return (
              <div
                key={t.key}
                onClick={() => setTipFilter(active ? '' : t.key)}
                style={{
                  background: active ? t.color : '#fff',
                  color: active ? '#fff' : '#333',
                  borderRadius: radius.md,
                  padding: '14px 16px',
                  cursor: 'pointer',
                  borderLeft: `4px solid ${t.color}`,
                  boxShadow: shadows.sm,
                  transition: 'all 0.15s',
                  userSelect: 'none',
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#f8f9fa'; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = '#fff'; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 600, opacity: active ? 0.95 : 0.7 }}>
                    {t.icon} {t.label}
                  </span>
                  <span style={{ fontSize: '11px', opacity: 0.7 }}>{formatPct(info.pay_pct)}</span>
                </div>
                <div style={{ fontSize: '18px', fontWeight: 700, marginBottom: '2px' }}>
                  {formatTL(info.ciro)} ₺
                </div>
                <div style={{ fontSize: '11px', opacity: active ? 0.85 : 0.6 }}>
                  {formatNum(info.adet)} adet · {info.urun_sayisi} çeşit
                </div>
              </div>
            );
          })}
        </div>
      )}

      {loading && <div style={{ padding: '40px', textAlign: 'center', color: colors.textSecondary }}>Yükleniyor...</div>}

      {!loading && data && data.urun_sayisi === 0 && (
        <div style={{ background: '#fff3cd', color: '#856404', padding: '16px', borderRadius: radius.md, marginBottom: '16px' }}>
          Bu tarih aralığında satış verisi bulunamadı. POS Satış sayfasından veri yüklediğinizden emin olun.
        </div>
      )}

      {!loading && data && data.urun_sayisi > 0 && (
        <>
          {/* Top 10 Bar Chart */}
          <div style={{ background: colors.surface, padding: '16px', borderRadius: radius.md, marginBottom: '16px', boxShadow: shadows.sm }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '15px', color: '#333' }}>Top 10 — En Çok Satan</h3>
            {top10.map((u, i) => (
              <div key={u.urun_adi} style={{ marginBottom: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '3px' }}>
                  <span style={{ fontWeight: '600' }}>{i + 1}. {u.urun_adi}</span>
                  <span><strong>{formatNum(u.adet)}</strong> adet · {formatTL(u.ciro)} ₺</span>
                </div>
                <div style={{ background: '#f0f0f0', height: '8px', borderRadius: radius.sm, overflow: 'hidden' }}>
                  <div style={{
                    width: `${(u.adet / maxAdet) * 100}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #1976d2, #42a5f5)',
                    transition: 'width 0.3s'
                  }} />
                </div>
              </div>
            ))}
          </div>

          {/* Tablo */}
          <div style={{ background: colors.surface, borderRadius: radius.md, boxShadow: shadows.sm, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <h3 style={{ margin: 0, fontSize: '15px', color: '#333' }}>Tüm Ürünler ({filteredUrunler.length})</h3>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setHideZeroRevenue(h => !h)}
                  style={{
                    padding: '6px 12px', borderRadius: radius.sm, border: 'none', cursor: 'pointer',
                    background: hideZeroRevenue ? '#1565c0' : '#e0e0e0',
                    color: hideZeroRevenue ? 'white' : '#555',
                    fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap'
                  }}
                  title="Cirosu 0 olan urunleri gizle/goster"
                >
                  {hideZeroRevenue ? 'Getirisizler Gizli' : 'Getirisiz Gizle'}
                </button>
                <select
                  value={tipFilter}
                  onChange={e => setTipFilter(e.target.value)}
                  style={{ ...inputStyle, width: '140px', marginBottom: 0 }}
                  title="Ürün tipine göre filtrele"
                >
                  <option value="">Tüm Tipler</option>
                  {URUN_TIPI_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Ürün ara..."
                  style={{ ...inputStyle, width: '180px', marginBottom: 0 }}
                />
                <select value={siralama} onChange={e => setSiralama(e.target.value)} style={{ ...inputStyle, width: '160px', marginBottom: 0 }}>
                  <option value="adet">Adet (Çoktan Aza)</option>
                  <option value="ciro">Ciro (Çoktan Aza)</option>
                  <option value="kar">Kâr (Çoktan Aza)</option>
                  <option value="kar_marj">Kâr Marjı (Çoktan Aza)</option>
                </select>
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: colors.backgroundAlt }}>
                    <th style={thStyle}>#</th>
                    <th style={{ ...thStyle, textAlign: 'left' }}>Ürün</th>
                    <th style={{ ...thStyle, textAlign: 'left' }}>Tip</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Adet</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Ort. Fiyat</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Ciro</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Maliyet</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Kâr</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Kâr %</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Pay %</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUrunler.map((u, i) => {
                    const tipBadge = {
                      ANA_URUN: { label: 'Ana', bg: '#ffebee', color: '#c62828' },
                      YAN_URUN: { label: 'Yan', bg: '#fff3e0', color: '#ef6c00' },
                      ICECEK: { label: 'İçecek', bg: '#e3f2fd', color: '#1565c0' },
                      TATLI: { label: 'Tatlı', bg: '#fce4ec', color: '#ad1457' },
                      DIGER: { label: 'Diğer', bg: '#eceff1', color: '#546e7a' },
                    }[u.urun_tipi] || { label: '-', bg: '#f5f5f5', color: '#999' };
                    return (
                    <tr key={u.urun_adi} style={{ borderBottom: `1px solid ${colors.border}` }}>
                      <td style={{ ...tdStyle, color: colors.textTertiary }}>{i + 1}</td>
                      <td style={{ ...tdStyle, fontWeight: '600' }}>{u.urun_adi}</td>
                      <td style={tdStyle}>
                        <span style={{ padding: '3px 8px', borderRadius: radius.sm, background: tipBadge.bg, color: tipBadge.color, fontSize: '11px', fontWeight: 600 }}>
                          {tipBadge.label}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNum(u.adet)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: colors.textSecondary }}>{formatTL(u.ort_fiyat)} ₺</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600', color: '#2e7d32' }}>{formatTL(u.ciro)} ₺</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: '#e65100' }}>{u.maliyet > 0 ? `${formatTL(u.maliyet)} ₺` : '-'}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: u.kar > 0 ? '#2e7d32' : '#999' }}>{u.kar > 0 ? `${formatTL(u.kar)} ₺` : '-'}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: u.kar_marj_pct > 0 ? '#2e7d32' : '#999' }}>{u.kar_marj_pct > 0 ? formatPct(u.kar_marj_pct) : '-'}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: colors.textSecondary }}>{formatPct(u.pay_pct)}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Trend */}
          {data.trend && data.trend.length > 0 && (
            <div style={{ background: colors.surface, padding: '16px', borderRadius: radius.md, marginTop: '16px', boxShadow: shadows.sm }}>
              <h3 style={{ margin: '0 0 12px', fontSize: '15px', color: '#333' }}>Dönem Trendi ({filtreler.gruplama === 'gun' ? 'Günlük' : filtreler.gruplama === 'hafta' ? 'Haftalık' : 'Aylık'})</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: colors.backgroundAlt }}>
                      <th style={thStyle}>Dönem</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Adet</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Ciro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.trend.map(t => (
                      <tr key={t.donem} style={{ borderBottom: `1px solid ${colors.border}` }}>
                        <td style={tdStyle}>{t.donem}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNum(t.adet)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', color: '#2e7d32', fontWeight: '600' }}>{formatTL(t.ciro)} ₺</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  if (embedded) return content;

  return (
    <div>
      <Header title="Ürün Satış Raporu" />
      <div style={{ background: colors.background, minHeight: 'calc(100vh - 60px)' }}>
        {content}
      </div>
    </div>
  );
};

const KpiCard = ({ label, value, suffix, color }) => (
  <div style={{ background: colors.surface, borderRadius: radius.md, padding: '16px', borderLeft: `4px solid ${color}`, boxShadow: shadows.sm }}>
    <div style={{ fontSize: '12px', color: colors.textSecondary, marginBottom: '4px' }}>{label}</div>
    <div style={{ fontSize: '22px', fontWeight: '700', color }}>{value} <span style={{ fontSize: '13px', fontWeight: '400', color: colors.textTertiary }}>{suffix}</span></div>
  </div>
);

const labelStyle = { display: 'block', fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '4px' };
const inputStyle = { width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '14px', boxSizing: 'border-box', marginBottom: 0 };
const thStyle = { padding: '6px 8px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#555', borderBottom: '2px solid #dee2e6' };
const tdStyle = { padding: '8px 12px', fontSize: '13px', verticalAlign: 'middle' };

export default UrunSatisRaporuPage;
